"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronDown, Mail, MapPin, Menu, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

type NavItem = {
  label: string;
  href: string;
};

type ServiceItem = {
  label: string;
  href: string;
};

type ServiceCategory = {
  label: string;
  items: ServiceItem[];
};

type ServiceArea = {
  label: string;
  href: string;
};

type ContactInfo = {
  address: string;
  addressHref: string;
  phone: string;
  phoneHref: string;
  email: string;
  emailHref: string;
};

type MobileHeaderMenuProps = {
  contactInfo: ContactInfo;
  navItems: NavItem[];
  serviceCategories: ServiceCategory[];
  serviceAreas: ServiceArea[];
  serviceCounties: string[];
};

export default function MobileHeaderMenu({
  contactInfo,
  navItems,
  serviceCategories,
  serviceAreas,
  serviceCounties,
}: MobileHeaderMenuProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openCategory, setOpenCategory] = useState(
    serviceCategories[0]?.label ?? "",
  );
  const [isServiceAreasOpen, setIsServiceAreasOpen] = useState(false);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsServiceAreasOpen(false);
    setOpenCategory("");
  };

  return (
    <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <SheetTrigger asChild className="xl:hidden">
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full text-gray-700 hover:bg-stone-100 hover:text-[#46473f]"
          aria-label="Open navigation menu"
        >
          <Menu className="h-7 w-7" />
        </Button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="w-[92vw] max-w-md border-0 bg-[#fcf9f2] p-0 sm:w-[420px]"
      >
        <div className="flex h-full flex-col">
          <header className="bg-[#46473f] px-6 py-8 pr-14 text-white">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-stone-300">
              Tampa Terrazzo
            </p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight">
              Explore our services
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-stone-200">
              Expert restoration and care for every surface.
            </p>
          </header>

          <div className="flex-1 overflow-y-auto px-5 py-6">
            <section aria-labelledby="mobile-services-heading">
              <div className="mb-3 flex items-center justify-between px-1">
                <h3
                  id="mobile-services-heading"
                  className="text-xs font-bold uppercase tracking-[0.16em] text-[#46473f]"
                >
                  Services
                </h3>
                <span className="text-xs text-stone-500">Choose a surface</span>
              </div>

              <div className="space-y-2">
                {serviceCategories.map((category) => {
                  const isOpen = openCategory === category.label;

                  return (
                    <div
                      key={category.label}
                      className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm"
                    >
                      <button
                        type="button"
                        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left transition-colors hover:bg-stone-50"
                        aria-expanded={isOpen}
                        onClick={() =>
                          setOpenCategory((current) =>
                            current === category.label ? "" : category.label,
                          )
                        }
                      >
                        <span>
                          <span className="block font-semibold text-[#46473f]">
                            {category.label}
                          </span>
                          <span className="mt-0.5 block text-xs text-stone-500">
                            {category.items.length} services
                          </span>
                        </span>
                        <span
                          className={`grid size-8 place-items-center rounded-full bg-stone-100 text-[#46473f] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                        >
                          <ChevronDown className="h-4 w-4" aria-hidden="true" />
                        </span>
                      </button>

                      {isOpen && (
                        <div className="grid gap-2 border-t border-stone-100 bg-stone-50 p-3 sm:grid-cols-2">
                          {category.items.map((service) => (
                            <Link
                              key={service.href}
                              href={service.href}
                              className="rounded-xl border border-transparent bg-white px-3 py-3 text-sm font-medium leading-snug text-stone-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-stone-200 hover:text-[#46473f] hover:shadow"
                              onClick={closeMenu}
                            >
                              {service.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            <section
              className="mt-8"
              aria-labelledby="mobile-service-areas-heading"
            >
              <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left transition-colors hover:bg-stone-50"
                  aria-expanded={isServiceAreasOpen}
                  aria-controls="mobile-service-areas-list"
                  onClick={() => setIsServiceAreasOpen((isOpen) => !isOpen)}
                >
                  <span>
                    <span
                      id="mobile-service-areas-heading"
                      className="block text-xs font-bold uppercase tracking-[0.16em] text-[#46473f]"
                    >
                      Service Areas
                    </span>
                    <span className="mt-1 block text-sm text-stone-500">
                      9 cities &amp; 3 counties across Tampa Bay Area
                    </span>
                  </span>
                  <span
                    className={`grid size-8 place-items-center rounded-full bg-stone-100 text-[#46473f] transition-transform duration-200 ${isServiceAreasOpen ? "rotate-180" : ""}`}
                  >
                    <ChevronDown className="h-4 w-4" aria-hidden="true" />
                  </span>
                </button>

                {isServiceAreasOpen && (
                  <div
                    id="mobile-service-areas-list"
                    className="border-t border-stone-100 bg-stone-50 p-3"
                  >
                    <div className="space-y-2">
                      {serviceAreas.map((area) => (
                        <Link
                          key={area.href}
                          href={area.href}
                          className="rounded-xl border border-transparent bg-white px-3 py-3 text-sm font-medium text-stone-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-stone-200 hover:text-[#46473f] hover:shadow"
                          onClick={closeMenu}
                        >
                          {area.label}
                        </Link>
                      ))}
                    </div>
                    <div className="mt-4 border-t border-stone-200 pt-3">
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#6b4f2d]">
                        Counties we serve
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-stone-500">
                        {serviceCounties.join(" · ")}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section className="mt-8" aria-labelledby="mobile-pages-heading">
              <h3
                id="mobile-pages-heading"
                className="mb-3 px-1 text-xs font-bold uppercase tracking-[0.16em] text-[#46473f]"
              >
                Explore
              </h3>
              <nav
                className="grid grid-cols-2 gap-2"
                aria-label="Site navigation"
              >
                {navItems.map((item) => (
                  <Link
                    key={item.label}
                    href={item.href}
                    className="rounded-xl border border-stone-200 bg-white px-3 py-3 text-sm font-medium text-stone-700 shadow-sm transition-colors hover:border-[#46473f] hover:text-[#46473f]"
                    onClick={closeMenu}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </section>

            <section
              className="mt-8 rounded-2xl bg-[#46473f] p-5 text-white"
              aria-labelledby="mobile-contact-heading"
            >
              <h3
                id="mobile-contact-heading"
                className="text-base font-semibold"
              >
                Talk with a specialist
              </h3>
              <div className="mt-4 space-y-3">
                <Link
                  href={contactInfo.phoneHref}
                  className="flex items-center gap-3 text-sm text-stone-100 transition-colors hover:text-white"
                >
                  <span className="grid size-8 place-items-center rounded-full bg-white/10">
                    <Phone className="h-4 w-4" aria-hidden="true" />
                  </span>
                  {contactInfo.phone}
                </Link>
                <Link
                  href={contactInfo.emailHref}
                  className="flex items-center gap-3 text-sm text-stone-100 transition-colors hover:text-white"
                >
                  <span className="grid size-8 place-items-center rounded-full bg-white/10">
                    <Mail className="h-4 w-4" aria-hidden="true" />
                  </span>
                  {contactInfo.email}
                </Link>
                <Link
                  href={contactInfo.addressHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-sm leading-relaxed text-stone-100 transition-colors hover:text-white"
                >
                  <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-full bg-white/10">
                    <MapPin className="h-4 w-4" aria-hidden="true" />
                  </span>
                  {contactInfo.address}
                </Link>
              </div>
            </section>
          </div>

          <div className="border-t border-stone-200 bg-white p-5">
            <Button
              asChild
              className="w-full rounded-xl bg-[#46473f] py-5 text-base font-semibold text-white shadow-md transition-all hover:bg-[#35362f]"
            >
              <Link href="/get-free-estimate" onClick={closeMenu}>
                Request a Free Estimate
              </Link>
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
